import { useState, useEffect, useMemo, Dispatch, SetStateAction, useContext } from "react";
import { useYouTrackProjectsWhenOpen } from "../../hooks/useYouTrackProjectsWhenOpen";
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  makeStyles,
  Checkbox,
  Body2,
} from "@fluentui/react-components";
import {
  AppButton,
  AppUserSelect,
  AppDateSelect,
  AppDivider,
  AppTimeInput,
  AppSelect,
  AppTextArea,
} from "../common";
import { Control, FieldValues, useForm } from "react-hook-form";
import usersService from "../../services/users.service";
import dataTimeService from "../../services/dataTime.service";
import { IUserSelectable } from "../../services/types/users";
import { applicationTypeEnum, assignmentStatus, documentTypeEnum, overtimeTypeEnum, userRoleEnum } from "../../utils/enums";
import appErrorToast from "../common/AppErrorToast";
import UserContext from "../../modules/AppRouter/UserContext";
import OvertimeAssigments from "../../services/overtimeAssigments.service";
import { IOvertimeAssigment } from "../../services/types/overtimeAssigments";
import { useParams } from "react-router-dom";
import { ContractTypeApi, isEmploymentContract, isPayoutOnlyContract } from "../../utils/contractType";
import {
  getAdminCommissionPayoutRateOptions,
  normalizePayoutRate,
  PAYOUT_RATE_UI,
} from "../../utils/payoutRate";
import {
  buildYouTrackOptionEntriesForEdit,
  formatYouTrackProjectLabel,
} from "../../utils/youtrackProject";

export type ModifyAssignmentEditScope =
  | "full"
  | "commissionTypeAfterRealized"
  | "employmentOvertimeAppAfterRealized";
const PAYOUT_TYPE = "Wypłata" as const;
const TIME_TO_COLLECT_TYPE = "Do odbioru" as const;

interface IFormData {
  employeeId: string;
  employeeContractType?: ContractTypeApi | null;
  documentType?: string;
  status?: string;
  assigmentDate: Date;
  duration: string;
  type: typeof PAYOUT_TYPE | typeof TIME_TO_COLLECT_TYPE | null;
  applicationType: string;
  assignmentDescription: string;
  generateApplication: boolean;
  payoutRate: string;
  changeJustification?: string;
  youTrackProjectId: string | null;
  youTrackProjectLabel: string | null;
}

interface IDialogProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  formData: IFormData;
  refresh: () => void;
  editScope?: ModifyAssignmentEditScope;
}

const useStyles = makeStyles({
  dialogContainer: {
    maxWidth: "450px",
  },
  readOnlyBlock: {
    marginBottom: "8px",
  },
});

const ModifyAssignmentDialog: React.FC<IDialogProps> = ({
  open,
  setOpen,
  formData,
  refresh,
  editScope = "full",
}) => {
  const styles = useStyles();
  const { id } = useParams<{ id: string }>();
  const { register, handleSubmit, reset, watch, setValue, control, formState: { errors } } = useForm<IFormData>();
  const [isLoading, setIsLoading] = useState(false);
  const { userData } = useContext(UserContext);
  const applicationTypeFieldValue = watch("applicationType");
  const typeFieldValue = watch("type");
  const selectedEmployeeId = watch("employeeId");
  const [userOptions, setUserOptions] = useState<IUserSelectable[]>([]);
  const isNarrowAfterRealized =
    editScope === "commissionTypeAfterRealized" ||
    editScope === "employmentOvertimeAppAfterRealized";
  const canUseNarrowAfterRealizedFlow =
    (editScope === "commissionTypeAfterRealized" &&
      formData.documentType === documentTypeEnum.Commision) ||
    (editScope === "employmentOvertimeAppAfterRealized" &&
      formData.documentType === documentTypeEnum.Application);
  const selectedEmployee = userOptions.find(
    (x) => x.id === (isNarrowAfterRealized ? formData.employeeId : selectedEmployeeId)
  );
  const effectiveContractType = selectedEmployee?.contractType ?? formData.employeeContractType ?? null;
  const isRealizedApplicationEditBlocked =
    userData.data.baseRole === userRoleEnum.Admin &&
    formData.documentType === documentTypeEnum.Application &&
    formData.status === assignmentStatus.Realized &&
    !(
      isEmploymentContract(effectiveContractType) &&
      formData.applicationType === applicationTypeEnum.Overtime
    );
  const showPayoutSavesAsCommissionInfo =
    userData.data.baseRole === userRoleEnum.Admin &&
    isEmploymentContract(effectiveContractType) &&
    formData.applicationType === applicationTypeEnum.Overtime &&
    formData.documentType === documentTypeEnum.Application &&
    typeFieldValue === PAYOUT_TYPE;
  const isPayoutOnlyEmployee =
    userData.data.baseRole === userRoleEnum.Admin
    && isPayoutOnlyContract(effectiveContractType);
  const typeOptions: Array<typeof PAYOUT_TYPE | typeof TIME_TO_COLLECT_TYPE> =
    isPayoutOnlyEmployee ? [PAYOUT_TYPE] : [PAYOUT_TYPE, TIME_TO_COLLECT_TYPE];
  const initialDurationValue = formData.duration.includes(":")
    ? formData.duration
    : dataTimeService.convertFormat(parseInt(formData.duration, 10));

  const isLeavingApplication = formData.applicationType === applicationTypeEnum.Leaving;
  const youTrackProjects = useYouTrackProjectsWhenOpen(open && !isLeavingApplication);

  useEffect(() => {
    usersService.selectableUsers().then((users) => {
      if (userData.data.baseRole === userRoleEnum.Employee) {
        users = users.filter((x) => x.id === userData.data.id);
      }
      setUserOptions(users);
    });
  }, [userData.data.baseRole, userData.data.id]);

  const applyFormDataToFields = () => {
    const shouldForcePayoutOnly = isPayoutOnlyEmployee && formData.applicationType === "Overtime";
    const initialType = shouldForcePayoutOnly ? PAYOUT_TYPE : formData.type;

    setValue("assignmentDescription", formData.assignmentDescription);
    setValue("generateApplication", formData.generateApplication);
    setValue(
      "applicationType",
      formData.applicationType === "Leaving" ? "Wyjście" : "Nadgodziny",
    );

    if (userData.data.baseRole === userRoleEnum.Admin) {
      setValue("type", initialType);
    }

    let initialPayout = normalizePayoutRate(formData.payoutRate);
    if (
      isEmploymentContract(effectiveContractType)
      && initialPayout === PAYOUT_RATE_UI.R100
    ) {
      initialPayout = PAYOUT_RATE_UI.R150;
    }
    setValue("payoutRate", initialPayout);

    setValue("employeeId", formData.employeeId);
    setValue("assigmentDate", formData.assigmentDate);
    setValue("duration", isNarrowAfterRealized ? formData.duration : initialDurationValue);
    if (isNarrowAfterRealized) {
      setValue("changeJustification", "");
    }
    setValue("youTrackProjectId", formData.youTrackProjectId ?? "");
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    applyFormDataToFields();
    if (userData?.data?.baseRole === userRoleEnum.Admin && userOptions.length > 0) {
      if (userOptions.find((x) => x.id === formData.employeeId)) {
        setValue("employeeId", formData.employeeId);
      }
    }
  }, [open, formData, userData, userOptions, isNarrowAfterRealized, setValue, effectiveContractType]);

  useEffect(() => {
    if (!open || !isPayoutOnlyEmployee) {
      return;
    }

    setValue("type", PAYOUT_TYPE);
  }, [open, isPayoutOnlyEmployee, selectedEmployeeId, setValue, isNarrowAfterRealized]);

  const projectOptionEntries = useMemo(
    () =>
      buildYouTrackOptionEntriesForEdit(
        youTrackProjects,
        formData.youTrackProjectId ?? undefined,
        formData.youTrackProjectLabel ?? undefined,
      ),
    [youTrackProjects, formData.youTrackProjectId, formData.youTrackProjectLabel],
  );

  async function handleFormSubmit(data: IFormData & { changeJustification?: string }) {
    if (isRealizedApplicationEditBlocked) {
      appErrorToast("Po realizacji zmiana odbiór/wypłata jest dostępna tylko dla zleceń.");
      return;
    }

    if (isNarrowAfterRealized && !canUseNarrowAfterRealizedFlow) {
      appErrorToast("Po realizacji typ rozliczenia można zmieniać tylko w obsługiwanym zakresie dokumentu.");
      return;
    }

    setIsLoading(true);

    const durationMinutes = isNarrowAfterRealized
      ? Number(formData.duration)
      : dataTimeService.convertTimeToMin(data.duration);

    if (!isNarrowAfterRealized && durationMinutes < 1) {
      appErrorToast("Ustaw prawidłową liczbę nadgodzin");
      setIsLoading(false);
      return;
    }

    const employeeId = isNarrowAfterRealized ? formData.employeeId : data.employeeId;
    const assignmentDate = isNarrowAfterRealized ? formData.assigmentDate : data.assigmentDate;
    const assignmentDescription = isNarrowAfterRealized
      ? formData.assignmentDescription
      : data.assignmentDescription;
    const generateApplication = isNarrowAfterRealized
      ? formData.generateApplication
      : data.generateApplication;

    const typeUi = isPayoutOnlyEmployee ? PAYOUT_TYPE : (data.type ?? formData.type);
    const payoutForApi = Number(data.payoutRate ?? formData.payoutRate) || 0;

    let youTrackProjectId = formData.youTrackProjectId;
    let youTrackProjectLabel = formData.youTrackProjectLabel;
    if (!isNarrowAfterRealized) {
      if (formData.applicationType === applicationTypeEnum.Leaving) {
        youTrackProjectId = formData.youTrackProjectId;
        youTrackProjectLabel = formData.youTrackProjectLabel;
      } else {
        youTrackProjectId = data.youTrackProjectId;
        const yt = youTrackProjects.find((p) => p.id === youTrackProjectId);
        youTrackProjectLabel = yt
          ? formatYouTrackProjectLabel(yt.shortName, yt.name)
          : formData.youTrackProjectLabel;
      }
    }

    const payload: IOvertimeAssigment = {
      employeeId,
      assignmentDate,
      duration: durationMinutes,
      type:
        typeUi === PAYOUT_TYPE
          ? overtimeTypeEnum.TimeToPay
          : overtimeTypeEnum.TimetoCollection,
      assignmentDescription,
      generateApplication,
      payoutRate: payoutForApi,
      asAdmin: userData.data.baseRole === userRoleEnum.Admin,
      youTrackProjectId,
      youTrackProjectLabel,
    };

    if (isNarrowAfterRealized) {
      const note = data.changeJustification?.trim();
      if (note) {
        payload.changeJustification = note;
      }
    }

    OvertimeAssigments.editAssigment(payload, id)
      .then(() => {
        reset();
        setOpen(false);
        refresh();
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  const handleApplicationTypeChanges = () => {
    if (applicationTypeFieldValue === "Nadgodziny") {
      setValue("generateApplication", false);
    }
  };

  const dialogTitle = isNarrowAfterRealized
    ? editScope === "employmentOvertimeAppAfterRealized"
      ? "Zmiana typu rozliczenia (wniosek UoP — zapis jako zlecenie)"
      : "Zmiana typu rozliczenia (zrealizowane zlecenie)"
    : userData.data.baseRole === userRoleEnum.Admin
      ? "Formularz zlecenia nadgodzin"
      : "Wniosek o nadgodziny/wyjście";

  return (
    <Dialog open={open} onOpenChange={(_, data) => setOpen(data.open)}>
      <DialogSurface className={styles.dialogContainer}>
        <form>
          <DialogBody>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogContent>
              <AppDivider />
              {isNarrowAfterRealized && userData.data.baseRole === userRoleEnum.Admin && (
                <>
                  <Body2 className={styles.readOnlyBlock}>
                    Zmieniasz wyłącznie typ (wypłata / do odbioru) i stawkę. Pozostałe dane muszą pozostać
                    zgodne z realizacją.
                  </Body2>
                  <AppDivider />
                </>
              )}
              {isRealizedApplicationEditBlocked && (
                <>
                  <Body2 className={styles.readOnlyBlock}>
                    Po realizacji zmiana odbioru/wypłaty jest dostępna tylko dla zleceń.
                  </Body2>
                  <AppDivider />
                </>
              )}
              {!isNarrowAfterRealized && (
                <>
                  <AppUserSelect
                    label="Wybierz pracownika"
                    options={userOptions}
                    formHandler={register("employeeId", {
                      required: true,
                    })}
                  />
                  <AppDivider />
                  {!isLeavingApplication && (
                    <>
                      <AppSelect
                        label="Projekt (YouTrack)"
                        optionEntries={projectOptionEntries}
                        formHandler={register("youTrackProjectId", { required: true })}
                      />
                      <AppDivider />
                    </>
                  )}
                  <AppDateSelect
                    label="Wybierz datę"
                    formHandler={register("assigmentDate", {
                      required: true,
                    })}
                    defaultValue={dataTimeService.dateToString(formData.assigmentDate)}
                  />
                  <AppDivider />
                  <AppTimeInput
                    type="time"
                    label="Liczba godzin"
                    formHandler={register("duration", {
                      required: { value: true, message: "Liczba godzin jest wymagana" },
                      min: 1,
                    })}
                    control={control as unknown as Control<FieldValues>}
                    errors={errors}
                    defaultValue={initialDurationValue}
                  />
                </>
              )}
              {userData.data.baseRole === userRoleEnum.Admin && (
                <>
                  {!isNarrowAfterRealized && <AppDivider />}
                  <AppSelect
                    label="Typ"
                    options={typeOptions}
                    formHandler={register("type", { required: true })}
                    disabled={isPayoutOnlyEmployee}
                  />
                  {showPayoutSavesAsCommissionInfo && (
                    <>
                      <AppDivider />
                      <Body2 className={styles.readOnlyBlock}>
                        Dla umowy o pracę wypłatę nadgodzin rozliczamy w systemie jako zlecenie. Po zapisie ten wpis
                        zostanie zapisany jako zlecenie, nie jako wniosek.
                      </Body2>
                    </>
                  )}
                  {isPayoutOnlyEmployee && (
                    <>
                      <AppDivider />
                      <Body2>
                        Dla osób na umowie B2B oraz umowie zlecenie, nie ma możliwości
                        zlecenia nadgodzin do odbioru.
                      </Body2>
                    </>
                  )}
                  {typeFieldValue === PAYOUT_TYPE && (
                    <>
                      <AppDivider />
                      <AppSelect
                        label="% wynagrodzenia"
                        options={[...getAdminCommissionPayoutRateOptions(effectiveContractType)]}
                        formHandler={register("payoutRate", {
                          required: true,
                        })}
                      />
                    </>
                  )}
                </>
              )}
              {userData.data.baseRole === userRoleEnum.Employee && !isNarrowAfterRealized && (
                <>
                  <AppDivider />
                  <AppSelect
                    label="Typ"
                    options={["Nadgodziny", "Wyjście"]}
                    formHandler={register("applicationType", { required: true })}
                    onClick={handleApplicationTypeChanges}
                    disabled
                  />
                  {applicationTypeFieldValue === "Wyjście" && (
                    <>
                      <AppDivider />
                      <Checkbox
                        label="Odrabiam tego samego dnia"
                        {...register("generateApplication")}
                        checked={watch("generateApplication")}
                        disabled
                      />
                    </>
                  )}
                </>
              )}
              {!isNarrowAfterRealized && (
                <>
                  <AppDivider />
                  <AppTextArea
                    label="Opis"
                    formHandler={register("assignmentDescription", { maxLength: 1000 })}
                  />
                </>
              )}
              {isNarrowAfterRealized && userData.data.baseRole === userRoleEnum.Admin && (
                <>
                  <AppDivider />
                  <AppTextArea
                    label="Uzasadnienie zmiany (opcjonalnie, do audytu)"
                    formHandler={register("changeJustification", { maxLength: 500 })}
                  />
                </>
              )}
            </DialogContent>
            <DialogActions>
              <AppButton
                text="Anuluj"
                appearance="secondary"
                onClick={() => {
                  setOpen(false);
                }}
              />
              <AppButton
                text="Zapisz"
                disabled={isLoading}
                appearance="primary"
                onClick={handleSubmit((data) => {
                  handleFormSubmit(data as IFormData & { changeJustification?: string });
                })}
              />
            </DialogActions>
          </DialogBody>
        </form>
      </DialogSurface>
    </Dialog>
  );
};

export default ModifyAssignmentDialog;
